import Button from '@/components/Button';
import { BsList, IoBugOutline } from '@/components/react-icons/main';
import Spinner from '@/components/Spinner';
import Tag from '@/components/Tag';
import getDateTimeAgo from '@/lib/getDateTimeAgo';
import sendToast from '@/lib/sendToast';
import { getAppVersions, publishApp } from '@/redux/actions/gravityActions';
import { getUser } from '@/redux/actions/settingsActions.js';
import { openAppIssueReportDrawer, setDrawerState } from '@/redux/actions/uiActions/drawerActions';
import { setModalState } from '@/redux/actions/uiActions/modalActions';
import waitForSyncReady from '@/redux/actions/waitForSyncReady';
import { useArgSelector } from '@/redux/curriedSelectors/curriedSelectors.js';
import selectors from '@/redux/selectors/selectors';
import appScreenSelectors from '@/screens/apps/redux/selectors/appScreenSelectors';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

const AppScreenPublishButtons = () => {
  const [isPublishing, setIsPublishing] = useState(false);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [createdByUser, setCreatedByUser] = useState(null);
  const [updatedByUser, setUpdatedByUser] = useState(null);

  const appId = useArgSelector(selectors.param, 'appId');
  const section = useArgSelector(selectors.param, 'section');
  const arg1 = useArgSelector(selectors.param, 'arg1');
  const appVersion = useArgSelector(selectors.param, 'version');
  const changesSincePublish = useSelector((state) => appScreenSelectors.appDiffs(state)?.length) || 0;
  const createdOn = useSelector((state) => selectors.appsMetadata(state)?.find((el) => el.id === appId)?.createdOn);
  const createdBy = useSelector((state) => selectors.appsMetadata(state)?.find((el) => el.id === appId)?.createdBy);
  const updatedBy = useSelector((state) => selectors.appsMetadata(state)?.find((el) => el.id === appId)?.updatedBy);
  const lastPublishedOn = useSelector((state) => selectors.appsMetadata(state)?.find((el) => el.id === appId)?.updatedOn);
  const hasPublishedVersion = useSelector((state) => !!selectors.appsMetadata(state)?.find((el) => el.id === appId)?.publishedVersion);
  const workflowActionUsage = useArgSelector(selectors.actionUsage, appId, arg1);
  const appIssues = useArgSelector(selectors.issuesByAppId, appId);
  const appVersions = useSelector((state) => state.workflow.stepAppVersions[appId]);
  const hasNoAppVersions = !appVersions || appVersions.length === 0;

  const unhandledIssues = appIssues?.filter((issue) => !issue?.handledOn);

  const wasNeverPublished = useMemo(() => !loadingVersions && hasNoAppVersions, [loadingVersions, hasNoAppVersions]);
  const lastPublishedOnAgo = lastPublishedOn && getDateTimeAgo(lastPublishedOn);
  const createdOnAgo = createdOn && getDateTimeAgo(createdOn);
  const lastPublishedDate = lastPublishedOn && `${new Date(lastPublishedOn).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: 'numeric' })}`;

  const handlePublishAppModalConfirm = async (data) => {
    if (data?.isPublished === true) {
      setIsPublishing(true);
    }

    await waitForSyncReady();

    publishApp(
      {
        appId,
        appVersion,
        versionIncrease: data?.versionIncrease,
        description: data?.description
      },
      (res) => {
        setIsPublishing(false);

        if (res?.publishedVersion) {
          getAppVersions({ appId });

          sendToast({
            message: `App published successfully as version ${res.publishedVersion}!`,
            type: 'success',
            duration: 4000
          });
        } else {
          sendToast({
            message: 'App publish failed!',
            type: 'error',
            duration: 4000
          });
        }
      }
    );
  };

  const handlePublishClick = () => {
    setModalState('publishApp', {
      open: true,
      mode: 'add',
      onChange: (data) => handlePublishAppModalConfirm(data)
    });
  };

  const handleIssuesClick = () => openAppIssueReportDrawer();

  const handlePublishDrawerOpen = () => {
    setDrawerState('appSnapshots', {
      open: true
    });
  };

  const loading = isPublishing || loadingVersions;
  const buttonActions = !wasNeverPublished ? [{ icon: loading ? Spinner : BsList, onClick: loading ? null : handlePublishDrawerOpen }] : [];

  useEffect(() => {
    if (appId && hasNoAppVersions) {
      setLoadingVersions(true);
      getAppVersions({ appId }, () => setLoadingVersions(false));
    }
  }, [appId, hasNoAppVersions]);

  useEffect(() => {
    if (createdBy && !hasPublishedVersion) {
      getUser({ id: createdBy }).then((res) => {
        setCreatedByUser(res?.[0]?.name || res?.[0]?.email);
      });
    }
  }, [createdBy, hasPublishedVersion]);

  useEffect(() => {
    if (updatedBy) {
      getUser({ id: updatedBy }).then((res) => {
        setUpdatedByUser(res?.[0]?.name || res?.[0]?.email);
      });
    }
  }, [updatedBy]);

  const tagText = hasPublishedVersion //
    ? updatedByUser
      ? `Last published by ${updatedByUser}${lastPublishedOnAgo ? ` ${lastPublishedOnAgo}` : ''}`
      : `Last published${lastPublishedOnAgo ? ` ${lastPublishedOnAgo}` : ''}`
    : createdByUser
      ? `Created by ${createdByUser}${createdOnAgo ? ` ${createdOnAgo}` : ''}`
      : `Created${createdOnAgo ? ` ${createdOnAgo}` : ''}`;

  return (
    <>
      <Tag variant={'warning'} size={'sm'} onClick={handlePublishClick}>
        Draft {changesSincePublish ? `- ${changesSincePublish} unpublished change${changesSincePublish > 1 ? 's' : ''}` : ''}
      </Tag>
      <Tag variant={'info'} size={'sm'} data-title={hasPublishedVersion ? lastPublishedDate : createdOn} onClick={handlePublishClick}>
        {tagText}
      </Tag>
      {workflowActionUsage > 0 && section === 'actions' && arg1 && (
        <Tag size="sm" data-title={`This is in use in ${workflowActionUsage} production workflows.`}>
          {workflowActionUsage} Workflows
        </Tag>
      )}
      {unhandledIssues?.length > 0 && (
        <Button className="error" size="sm" icon={IoBugOutline} onClick={handleIssuesClick}>
          {`${unhandledIssues?.length} ${unhandledIssues?.length === 1 ? 'Issue' : 'Issues'}`}
        </Button>
      )}
      <Button size="sm" onClick={handlePublishClick} disabled={isPublishing} loading={isPublishing} actions={buttonActions}>
        Publish
      </Button>
    </>
  );
};

export default AppScreenPublishButtons;
